import * as React from 'react';
import type { ElectiveGroup } from '../../ViewModel/StudyPlanner/ElectiveGroup.csharp';
import type { IOnlineStudyPlannerSectionBlockViewModel } from '../../ViewModel/StudyPlanner/IOnlineStudyPlannerSectionBlockViewModel.csharp';
import type { LegacyOnlineStudyPlannerSectionBlockViewModel } from '../../ViewModel/StudyPlanner/Legacy/LegacyOnlineStudyPlannerSectionBlockViewModel.csharp';
import type { OnlineStudyPlannerSectionBlockViewModel } from '../../ViewModel/StudyPlanner/OnlineStudyPlannerSectionBlockViewModel.csharp';
import { isLegacyView, isNewView } from '../Utils/OnlineStudyPlannerSectionBlockUtils';
import { ThemeContextProvider } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type {
    ElectiveGroupCode,
    ElectiveGroupDescriptor,
} from '~/Features/StudyPage/Online/Components/Planner/Context/types';
import { OnlineStudyPlannerSection } from '~/Features/StudyPage/Online/Sections/OnlineStudyPlannerSection/OnlineStudyPlannerSection';
import { OnlineStudyPlan } from '~/Pages/OnlineStudyPage/Subjects/OnlineStudyPlan';
import { themeRoundedCornersValues } from '~/Utils/Theme';

// 'Block' preserved in the name in order to smoothly migrate it in the future for section-based approach on study page.
export const OnlineStudyPlannerSectionBlock: React.FC<IOnlineStudyPlannerSectionBlockViewModel> = props => {
    const { type } = props;

    if (isLegacyView(props)) {
        return <LegacyOnlineStudyPlanner {...props} />;
    } else if (isNewView(props)) {
        return <OnlineStudyPlanner {...props} />;
    } else {
        throw new Error(`Unsupported study model section: ${type}`);
    }
};

const LegacyOnlineStudyPlanner: React.FC<LegacyOnlineStudyPlannerSectionBlockViewModel> = props => {
    const { onlineStudyPlanModel } = props;

    return <OnlineStudyPlan {...onlineStudyPlanModel} />;
};

const OnlineStudyPlanner: React.FC<OnlineStudyPlannerSectionBlockViewModel> = props => {
    const {
        id,
        heading,
        teaser,
        timeDetails,
        nonPurchasableSubjectsLink,
        subjects,
        electiveGroupNames,
        popup,
        translations,
        onlineApplicationFormUrl,
    } = props;

    if (!heading) {
        throw new Error('Heading must be defined for study planner');
    }

    const mapElectiveGroupNames = (electiveGroups: ElectiveGroup[]) =>
        new Map<ElectiveGroupCode, ElectiveGroupDescriptor>(
            electiveGroups.map(group => [group.code, { label: group.name }])
        );

    return (
        <ThemeContextProvider theme={themeRoundedCornersValues}>
            <OnlineStudyPlannerSection
                id={id}
                heading={heading}
                teaser={teaser}
                subjects={subjects.map(subject => ({
                    ...subject,
                    startDate: subject.startDate ? new Date(subject.startDate) : undefined,
                }))}
                nonPurchasableSubjectsLink={nonPurchasableSubjectsLink}
                timeDetails={timeDetails}
                electiveGroupDescriptors={mapElectiveGroupNames(electiveGroupNames)}
                onlineApplicationFormUrl={onlineApplicationFormUrl}
                popup={popup}
                translations={translations}
            />
        </ThemeContextProvider>
    );
};
